import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Inject, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import { GENERIC_AUTH_SERVICE_TOKEN, GenericAuthService } from '@nk/auth-lib';
import { ButtonDirective, CardComponent, SpinnerComponent } from '@nk/ui-lib';
import { arrayToDict } from '@nk/utils-lib';
import { of } from 'rxjs';
import { filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { DEFAULT_TRIAL_PERIOD, SignUpService } from '../../services/sign-up.service';

@Component({
  selector: 'nk-sign-up',
  imports: [CommonModule, RouterModule, TranslateModule, SpinnerComponent, CardComponent, ButtonDirective],
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
})
export class SignUpComponent implements OnInit {
  public setupStage = 0;
  public pathPrefix: string;
  public stageList: { showErrors: boolean; path: string; order: number; label: string; form: FormGroup }[];

  public firstRouteChecked = false;
  public currentStage: {
    showErrors: boolean;
    path: string;
    order: number;
    label: string;
    form: FormGroup;
  };
  public isLoggedIn = null;
  public enableSelfService: boolean;
  public loadingSelfServiceStatus: boolean;
  public trialPeriod = DEFAULT_TRIAL_PERIOD;
  private _destroy = inject(DestroyRef);

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _signUpService: SignUpService,
    private _appService: AppService,
    @Inject(GENERIC_AUTH_SERVICE_TOKEN) private _authService: GenericAuthService,
  ) {}

  public ngOnInit(): void {
    this.loadingSelfServiceStatus = true;

    this._appService
      .fetchConfigList()
      .pipe(
        switchMap((config) => {
          const configDict = arrayToDict(config, (a) => a.config_key);
          if (configDict.self_service_onboarding_enabled.config_value === false) {
            const secretUuid = this._activatedRoute.snapshot.queryParams['secret_uuid'];
            if (secretUuid) {
              this._signUpService.secretUuid = secretUuid;
              return this._signUpService.checkOnboardingPreflight(null, null, secretUuid);
            } else {
              this.enableSelfService = false;
              this.loadingSelfServiceStatus = false;
            }
          } else {
            this.enableSelfService = true;
            this.loadingSelfServiceStatus = false;
          }
          return of(null);
        }),
      )
      .subscribe((payload) => {
        if (payload) {
          this.enableSelfService = payload.secret_uuid_rejected !== true;
          this.loadingSelfServiceStatus = false;
        }
      });

    this._authService.authInfo$.subscribe((authInfo) => {
      if (authInfo.isValid()) {
        this.isLoggedIn = true;
      } else {
        this.isLoggedIn = false;
      }
    });

    this.stageList = this._signUpService.stageList;
    this.pathPrefix = this._signUpService.pathPrefix;

    // Make sure user STARTS at the beginning.
    this._activatedRoute.firstChild.url // this is not triggered after initial load. See events subscription under.
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe((childRoute) => {
        if (childRoute && childRoute[0]) {
          const currentStage = this.stageList.find((a) => a.path === childRoute[0].path);
          if (currentStage) {
            this.currentStage = currentStage;
          } else {
            this.navigateToBeginning();
          }
          // if cant find currentStage. return to beginning of step.
        }

        // MAKE SURE USER STARTS AT BEGINNING
        // first time we initialize the page and we have a child route active, we need to check that we are on the beginning of the stepper.
        if (childRoute && childRoute[0] && !this.firstRouteChecked) {
          this.firstRouteChecked = true;
          const routerPath = childRoute[0].path;
          const order = this.stageList.find((a) => a.path === routerPath).order;
          if (order !== 0) {
            this.navigateToBeginning();
          }
        }
      });

    // happens after first initial navigation event.
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        mergeMap(() => {
          return this._activatedRoute.firstChild.url;
        }),
      )
      .subscribe((childRoute) => {
        if (childRoute && childRoute[0]) {
          const currentStage = this.stageList.find((a) => a.path === childRoute[0].path);
          if (currentStage) {
            this.currentStage = currentStage;
          } else {
            this.navigateToBeginning();
          }
        }
      });
  }
  public navigateToBeginning(): void {
    const firstStage = this.stageList.find((a) => a.order === 0);
    // show message and return user to beginning of flow.
    void this._router.navigate([this.pathPrefix + '/' + firstStage.path]);
  }

  public onPrevClick(): void {
    if (this.setupStage > 0) {
      this.setupStage--;
      const stage = this.stageList.find((a) => a.order === this.setupStage);
      if (stage) {
        void this._router.navigate([this.pathPrefix + '/' + stage.path], { queryParamsHandling: 'preserve' });
      }
    }
  }
  /// assumes order is same as stagelist array index.
  public onNextClick(): void {
    if (this.currentStage.form.valid) {
      const companyUuid = this.currentStage.form.controls.company_uuid?.value;
      const companyName = this.currentStage.form.controls.company_name?.value;
      const phoneNumber = this.currentStage.form.controls.phone_number?.value;
      const email = this.currentStage.form.controls.email?.value;
      if (this.currentStage.path === 'company' && companyUuid) {
        this._signUpService
          .checkOnboardingPreflight(email, companyUuid, null)
          .pipe(
            switchMap((data) => {
              if (data.duplicate_company_flag) {
                return this._signUpService
                  .postOnboardingExistingCompany({
                    company_uuid: companyUuid,
                    phone_number: phoneNumber,
                    email,
                  })
                  .pipe(map(() => 'navigate-sign-up-existing'));
              } else {
                return of('navigate-next');
              }
            }),
            takeUntilDestroyed(this._destroy),
          )
          .subscribe((result) => {
            if (result === 'navigate-sign-up-existing') {
              void this._router.navigate(['sign-up-existing'], { queryParams: { companyName: companyName } });
            } else {
              this._navigateNext();
            }
          });
      } else {
        this._navigateNext();
      }
    } else {
      this._signUpService.setShowError(true);
    }
  }
  private _navigateNext(): void {
    this.setupStage++;
    const stage = this.stageList.find((a) => a.order === this.setupStage);
    if (stage) {
      void this._router.navigate([this.pathPrefix + '/' + stage.path], { queryParamsHandling: 'preserve' });
    }
  }

  public onLogout(): void {
    const currentUrl = window.location.href;
    this._authService.logout('custom', `You can now continue signing up at: <a href="${currentUrl}">${currentUrl}</a>`);
  }
}
